/**
 * SiteMotion.jsx — the moving parts the home and How It Works pages share.
 *
 *   useReducedMotion()   prefers-reduced-motion, read after mount (SSR-safe)
 *   useFit(base, cap)    scale for a fixed-size product canvas: min(cap, width / base)
 *   useStepLoop(ms, end) a stepped loop with play/pause and jump-to-step
 *   useHashScroll()      land on #anchor after a cross-page navigation
 *   <StepControls/>      play/pause · step pills · mono caption
 *
 * Product visuals are fixed-size canvases scaled to their container, never
 * reflowed: the container reserves the height with aspect-ratio (no layout
 * shift before hydration) and the canvas gets transform: scale(useFit()).
 */
import { useEffect, useRef, useState } from 'react';
import { AUTOPLAY } from './siteConfig.js';

export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return reduced;
}

/** [ref, scale] — attach ref to the canvas's container; scale is 1 until measured. */
export function useFit(base, cap = 1) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      if (w) setScale(Math.min(cap, Math.round((w / base) * 1000) / 1000));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [base, cap]);
  return [ref, scale];
}

/**
 * A loop of `durations.length` steps. Reduced motion (or AUTOPLAY off) never
 * autoplays and rests on `restStep`, the frame that explains the most alone.
 */
export function useStepLoop(durations, restStep) {
  const reduced = useReducedMotion();
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [jump, setJump] = useState(0);
  useEffect(() => {
    if (reduced || !AUTOPLAY) { setPlaying(false); setStep(restStep); }
  }, [reduced, restStep]);
  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => setStep((s) => (s + 1) % durations.length), durations[step]);
    return () => clearTimeout(t);
  }, [step, playing, jump, durations]);
  return {
    step,
    playing,
    toggle: () => setPlaying((p) => !p),
    // A jump restarts the step's full duration, even onto the current step.
    go: (i) => { setStep(i); setJump((n) => n + 1); },
  };
}

/** On mount: a #hash in the URL scrolls to its section (SPA arrivals skip the browser's own jump). */
export function useHashScroll() {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return undefined;
    let el = null;
    try { el = document.getElementById(decodeURIComponent(window.location.hash.slice(1))); } catch (_) { /* bad hash */ }
    if (!el) return undefined;
    const t = setTimeout(() => el.scrollIntoView({ block: 'start' }), 50);
    return () => clearTimeout(t);
  }, []);
}

/**
 * The step pills are 6px marks 5px apart: too small to be separate touch
 * targets. So the row is ONE control — a slider the keyboard steps through
 * (←/→, Home/End) — and each pill is still a click target for the mouse.
 */
export function StepControls({ step, playing, onToggle, onGo, caps, label, className = '' }) {
  const last = caps.length - 1;
  const onKey = (e) => {
    const to = { ArrowRight: step + 1, ArrowUp: step + 1, ArrowLeft: step - 1, ArrowDown: step - 1, Home: 0, End: last }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    onGo(Math.max(0, Math.min(last, to)));
  };
  return (
    <div className={`s-steps ${className}`}>
      <button type="button" className="s-play" onClick={onToggle} aria-label={playing ? `Pause ${label}` : `Play ${label}`}>
        <span aria-hidden="true">{playing ? '❚❚' : '▶'}</span>
      </button>
      <span className="s-pills" role="slider" tabIndex={0} aria-label={`Step of ${label}`} aria-valuemin={1} aria-valuemax={caps.length}
        aria-valuenow={step + 1} aria-valuetext={`${step + 1} of ${caps.length}: ${caps[step].replace(/^\d+ · /, '').toLowerCase()}`} onKeyDown={onKey}>
        {caps.map((c, i) => <i key={c} className={i === step ? 'on' : undefined} onClick={() => onGo(i)} />)}
      </span>
      <span className="s-steps-cap" aria-hidden="true">{caps[step]}</span>
    </div>
  );
}
